import { getSupabase, isSupabaseConfigured } from 'shared/supabase/client';
import { fetchWithFallback } from 'shared/data/fetchWithFallback';

export type AnnouncementScope = 'batch' | 'institution' | 'global';
export type AnnouncementVariant = 'info' | 'promo' | 'urgent' | 'success';

export type Announcement = {
  id: string;
  institutionId: string;
  batchId: string | null;
  scope: AnnouncementScope;
  variant: AnnouncementVariant;
  title: string;
  body: string;
  ctaLabel: string | null;
  ctaUrl: string | null;
  dismissible: boolean;
  priority: number;
  startsAt: string | null;
  endsAt: string | null;
};

export type AnnouncementInboxItem = Announcement & {
  isRead: boolean;
  shortBody: string;
  createdAt: string;
};

function mapRow(row: Record<string, unknown>): Announcement {
  return {
    id: String(row.id),
    institutionId: String(row.institution_id),
    batchId: row.batch_id ? String(row.batch_id) : null,
    scope: String(row.scope) as AnnouncementScope,
    variant: String(row.variant) as AnnouncementVariant,
    title: String(row.title ?? ''),
    body: String(row.body ?? ''),
    ctaLabel: row.cta_label ? String(row.cta_label) : null,
    ctaUrl: row.cta_url ? String(row.cta_url) : null,
    dismissible: Boolean(row.dismissible ?? true),
    priority: Number(row.priority ?? 0),
    startsAt: row.starts_at ? String(row.starts_at) : null,
    endsAt: row.ends_at ? String(row.ends_at) : null,
  };
}

function mapInboxRow(row: Record<string, unknown>): AnnouncementInboxItem {
  const base = mapRow(row);
  const shortBody = row.short_body ? String(row.short_body) : base.body.slice(0, 140);
  return {
    ...base,
    isRead: Boolean(row.is_read),
    shortBody,
    createdAt: row.created_at ? String(row.created_at) : '',
  };
}

async function listVisibleFromSupabase(limit = 5): Promise<Announcement[]> {
  const { data, error } = await getSupabase().rpc('inst_announcement_list_visible', {
    p_limit: limit,
  });
  if (error) throw new Error(error.message);
  if (!data?.ok) {
    throw new Error((data as { error?: string })?.error || 'Failed to load announcements');
  }
  const items = Array.isArray(data.items) ? data.items : [];
  return items.map((row: Record<string, unknown>) => mapRow(row));
}

export async function listVisibleAnnouncements(limit = 5): Promise<Announcement[]> {
  if (!isSupabaseConfigured) return [];
  return fetchWithFallback(
    () => listVisibleFromSupabase(limit),
    async () => []
  );
}

async function listInboxFromSupabase(limit = 20): Promise<AnnouncementInboxItem[]> {
  const { data, error } = await getSupabase().rpc('inst_announcement_list_inbox', {
    p_limit: limit,
  });
  if (error) throw new Error(error.message);
  if (!data?.ok) {
    throw new Error((data as { error?: string })?.error || 'Failed to load announcement inbox');
  }
  const items = Array.isArray(data.items) ? data.items : [];
  return items.map((row: Record<string, unknown>) => mapInboxRow(row));
}

export async function listAnnouncementInbox(limit = 20): Promise<AnnouncementInboxItem[]> {
  if (!isSupabaseConfigured) return [];
  return fetchWithFallback(
    () => listInboxFromSupabase(limit),
    async () => []
  );
}

export async function markAnnouncementRead(id: string): Promise<void> {
  if (!isSupabaseConfigured) return;
  const { data, error } = await getSupabase().rpc('inst_announcement_mark_read', {
    p_id: id,
  });
  if (error) throw new Error(error.message);
  if (!data?.ok) {
    throw new Error((data as { error?: string })?.error || 'Failed to mark announcement read');
  }
}

export async function dismissAnnouncement(id: string): Promise<void> {
  if (!isSupabaseConfigured) return;
  const { data, error } = await getSupabase().rpc('inst_announcement_dismiss', {
    p_id: id,
  });
  if (error) throw new Error(error.message);
  if (!data?.ok) {
    throw new Error((data as { error?: string })?.error || 'Failed to dismiss');
  }
}
